import { useEffect, useRef, useCallback } from "react";

/**
 * Custom hook for infinite scroll using IntersectionObserver with debouncing.
 *
 * @param {object} options - Hook options
 * @param {boolean} options.hasMore - Whether more items are available to load
 * @param {boolean} options.loading - Whether currently loading more items
 * @param {function} options.onLoadMore - Callback to load more items
 * @param {string} options.rootMargin - IntersectionObserver rootMargin (default: "800px")
 * @param {number} options.cooldownMs - Cooldown between loads in ms (default: 800)
 * @param {boolean} options.enabled - Whether infinite scroll is enabled (default: true)
 * @returns {object} - { sentinelRef } ref to attach to sentinel element
 */
export function useInfiniteScroll({
  hasMore,
  loading,
  onLoadMore,
  rootMargin = "800px",
  cooldownMs = 800,
  enabled = true,
}) {
  const sentinelRef = useRef(null);
  const cooldownRef = useRef(false);
  const observerRef = useRef(null);

  // Stable callback ref to avoid recreating observer on every render
  const loadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const handleIntersection = useCallback(
    (entries) => {
      const [entry] = entries;
      if (!entry.isIntersecting) return;
      if (!hasMore || loading || cooldownRef.current) return;

      cooldownRef.current = true;
      loadMoreRef.current?.();

      setTimeout(() => {
        cooldownRef.current = false;
      }, cooldownMs);
    },
    [hasMore, loading, cooldownMs]
  );

  useEffect(() => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled) return undefined;

    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin,
      threshold: 0,
    });

    observerRef.current.observe(sentinel);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [handleIntersection, rootMargin, enabled]);

  return { sentinelRef };
}

export default useInfiniteScroll;
